import { useState, useEffect } from 'react';
import DateFilter from '@/pages/Management/SupportRequests/filters/DateFilter';
import FundFilter from '@/components/filters/FundFilter/FundFilter';
import PageSizeSelector from '@/components/PageSizeSelector/PageSizeSelector';
import OptionsRangeFilter, { FilterOption } from '@/components/filters/OptionsRangeFilter/OptionsRangeFilter';
import { OfflineDonationStatus } from '@/pages/Donation/DonationsAdmin/types';
import styles from '@/components/filters/FiltersArea.module.scss';

const STATUS_OPTIONS: { id: OfflineDonationStatus; label: string }[] = [
  { id: 'Pending', label: 'donations.management.status.Pending' },
  { id: 'InProgress', label: 'donations.management.status.InProgress' },
  { id: 'Approved', label: 'donations.management.status.Approved' },
  { id: 'Rejected', label: 'donations.management.status.Rejected' },
  { id: 'FutureCheck', label: 'donations.management.status.FutureCheck' },
];

const TYPE_OPTIONS: { id: string; label: string }[] = [
  { id: 'Check', label: 'donations.methods.Check' },
  { id: 'BankTransfer', label: 'donations.methods.ManualBankTransfer' },
];

interface Props {
  statusFilter: OfflineDonationStatus | null;
  setStatusFilter: (v: OfflineDonationStatus | null) => void;
  typeFilter: string | null;
  setTypeFilter: (v: string | null) => void;
  fundId: number | null;
  setFundId: (v: number | null) => void;
  dateRange: { from: string; to: string } | null;
  setDateRange: (v: { from: string; to: string } | null) => void;
  pageSize: number;
  setPageSize: (v: number) => void;
}

const FiltersArea = ({
  statusFilter, setStatusFilter,
  typeFilter, setTypeFilter,
  fundId, setFundId,
  dateRange, setDateRange,
  pageSize, setPageSize,
}: Props) => {
  const [statusOptions, setStatusOptions] = useState<FilterOption[]>(
    STATUS_OPTIONS.map((s) => ({ id: s.id, label: s.label, checked: false }))
  );

  const [typeOptions, setTypeOptions] = useState<FilterOption[]>(
    TYPE_OPTIONS.map((s) => ({ id: s.id, label: s.label, checked: false }))
  );

  useEffect(() => {
    setStatusOptions((prev) =>
      prev.map((o) => ({ ...o, checked: o.id === statusFilter }))
    );
  }, [statusFilter]);

  useEffect(() => {
    setTypeOptions((prev) =>
      prev.map((o) => ({ ...o, checked: o.id === typeFilter }))
    );
  }, [typeFilter]);

  const handleStatusChange = (id: string) => {
    setStatusFilter(statusFilter === id ? null : id as OfflineDonationStatus);
  };

  const handleTypeChange = (id: string) => {
    setTypeFilter(typeFilter === id ? null : id);
  };

  return (
    <div className={styles.filtersArea}>
      <div className={styles.filtersContainer}>
        <DateFilter dateRange={dateRange} onDateRangeChange={setDateRange} />
        <OptionsRangeFilter
          title="donations.management.col.status"
          filterType="options"
          options={statusOptions}
          onOptionChange={handleStatusChange}
          selectionType="radio"
        />
        <OptionsRangeFilter
          title="donations.management.col.type"
          filterType="options"
          options={typeOptions}
          onOptionChange={handleTypeChange}
          selectionType="radio"
        />
        <FundFilter selectedFundId={fundId} onFundChange={setFundId} />
      </div>
      <div className={styles.controlsContainer}>
        <PageSizeSelector value={pageSize} onChange={setPageSize} />
      </div>
    </div>
  );
};

export default FiltersArea;
